import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  AbilityEffect,
  AbilityLimitation,
  AttachedTable,
  ConsumedResource,
  ExtraEffectsFeature, 
  SpellLimitation,
  UpgradableNumber,
  WeaponLimitation, 
} from "@dndModels";
import { 
  HAPPENS_WHEN,
  TIME_RESTRICTIONS 
} from "@dndModels/Constants";
import { HappensWhen, PotenceType, TimeRestriction } from "@dndModels/Types";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import WeaponLimitInput from "@dndComponents/limitations/WeaponLimitInput";
import AbilityLimitInput from "@dndComponents/limitations/AbilityLimitInput";
import SpellLimitInput from "@dndComponents/limitations/SpellLimitInput";
// import ToggleButtonBox from '../../../../components/input/buttons/ToggleButtonBox';
// import AbilityEffectInput from './AbilityEffectInput';
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
import StringBox from "@components/input/strings/StringBox";
// import SelectResourceBox from '../select/SelectResourceBox';
import EffectsInput from "@dndComponents/feature/EffectsInput";
import ConsumedResourceInput from "@dndComponents/feature/ConsumedResourceInput";
import { FeatureSource } from '@models';

interface Props { 
  obj: ExtraEffectsFeature;
  feature_source?: FeatureSource;
  onChange: (changed: ExtraEffectsFeature) => void;
  disabled: boolean;
  attached_table?: AttachedTable;
}

export default function ExtraEffectsFeatureInput({ 
  obj,
  feature_source,
  onChange,
  disabled,
  attached_table
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [ping, setPing] = useState(false);
  const [myObj, setMyObj] = useState(obj.clone());
  // const [show_effect, setShowEffect] = useState("");
  // const [special_refresh_rule, setSpecialRefreshRule] = useState(false);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  // console.log(myObj);
  // console.log(obj);
  // const refresh_rule_helper = REFRESH_RULES.includes(obj.special_resource_refresh_rule) ? obj.special_resource_refresh_rule : "Special";
  return (
    <Grid container spacing={0} direction="row">
      <Grid size={3}>
        <SelectStringBox 
          name="Applies To"
          options={["Weapons","Abilities","Spells"]}
          values={myObj.apply_to}
          multiple
          onChange={(changed: ("Weapons" | "Abilities" | "Spells")[]) => {
            const new_obj = myObj;
            new_obj.apply_to = changed;
            setMyObj(new_obj);
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid size={3}>
        <SelectStringBox 
          name="How Often?"
          options={TIME_RESTRICTIONS}
          value={myObj.time_restriction}
          onChange={(changed: TimeRestriction) => {
            const new_obj = myObj;
            new_obj.time_restriction = changed;
            setMyObj(new_obj);
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid size={3}>
        <SelectStringBox 
          name="Happens When?"
          options={HAPPENS_WHEN}
          value={myObj.happens_when}
          onChange={(changed: HappensWhen) => {
            const new_obj = myObj;
            new_obj.happens_when = changed;
            setMyObj(new_obj);
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid size={3}>
        <StringBox 
          name="Extra Restriction"
          value={myObj.extra_restriction}
          onChange={(changed: string) => {
            const new_obj = myObj;
            new_obj.extra_restriction = changed;
            setMyObj(new_obj);
            onChange(new_obj);
          }}
        />
      </Grid>
      { myObj.apply_to.includes("Weapons") && 
        <Grid size={12}>
          <WeaponLimitInput
            obj={myObj.weapon_limit}
            disabled={disabled}
            onChange={(changed: WeaponLimitation) => {
              const new_obj = myObj;
              new_obj.weapon_limit = changed.clone();
              setMyObj(new_obj);
              onChange(new_obj);
            }}
          />
        </Grid>
      }
      { myObj.apply_to.includes("Abilities") && 
        <Grid size={12}>
          <AbilityLimitInput
            obj={myObj.ability_limit}
            disabled={disabled}
            onChange={(changed: AbilityLimitation) => {
              const new_obj = myObj;
              new_obj.ability_limit = changed.clone();
              setMyObj(new_obj);
              onChange(new_obj);
            }}
          />
        </Grid>
      }
      { myObj.apply_to.includes("Spells") && 
        <Grid size={12}>
          <SpellLimitInput
            obj={myObj.spell_limit}
            disabled={disabled}
            onChange={(changed: SpellLimitation) => {
              const new_obj = myObj;
              new_obj.spell_limit = changed.clone();
              setMyObj(new_obj);
              onChange(new_obj);
            }}
          />
        </Grid>
      }
      <Grid
        size={{
          xs: myObj.potence_type === "Class" ? 6 : 12
        }}>
        <SelectStringBox 
          name="Potence Type"
          options={["Slot","Character","Class","None"]}
          value={myObj.potence_type} 
          disabled={disabled}
          onChange={(changed: PotenceType) => {
            const floating_feature = myObj;
            floating_feature.potence_type = changed;
            setMyObj(floating_feature);
            onChange(floating_feature);
          }}
        />
      </Grid>
      { myObj.potence_type === "Class" && 
        <Grid
          size={{
            xs: 6
          }}>
          <SelectObjectBox
            name="Potence Class"
            data_type="game_class"
            value={myObj.potence_class}
            disabled={disabled}
            onChange={(changed: string) => {
              const floating_feature = myObj;
              floating_feature.potence_class = changed;
              setMyObj(floating_feature);
              onChange(floating_feature);
            }}
          />
        </Grid>
      }
      <Grid
        size={{
          xs: 6
        }}>
        <UpgradableNumberBox 
          name="Attack Bonus"
          value={myObj.attack_bonus} 
          disabled={disabled}
          attached_table={attached_table}
          onChange={(changed: UpgradableNumber) => {
            const floating_feature = myObj;
            floating_feature.attack_bonus = changed;
            setMyObj(floating_feature);
            onChange(floating_feature);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <UpgradableNumberBox 
          name="Save DC"
          value={myObj.save_dc} 
          disabled={disabled}
          attached_table={attached_table}
          onChange={(changed: UpgradableNumber) => {
            const floating_feature = myObj;
            floating_feature.save_dc = changed;
            setMyObj(floating_feature);
            onChange(floating_feature);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <EffectsInput
          effects={obj.effects}
          disabled={disabled}
          for_obj={obj}
          feature_source={feature_source}
          onChange={(changed: AbilityEffect[]) => {
            const new_obj = obj;
            obj.effects = changed;
            onChange(new_obj);
            setMyObj(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <ConsumedResourceInput 
          obj={obj.consumed_resource} 
          disabled={false}
          onChange={(changed: ConsumedResource) => {
            const new_obj = obj;
            new_obj.consumed_resource = changed;
            onChange(new_obj);
            setReloading(true);
          }}
        />
      </Grid>
    </Grid>
  );
}
